import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, DollarSign, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import ConfirmModal from '../../components/ConfirmModal';

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    category: 'infrastructure',
    description: '',
    amount: '',
    currency: 'USD',
    frequency: 'monthly',
    is_recurring: true,
    date_added: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/expenses');
      setExpenses(res.data.expenses || []);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/admin/expenses/${editingId}`, form);
        toast.success('Expense updated');
      } else {
        await api.post('/api/admin/expenses', form);
        toast.success('Expense added');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ category: 'infrastructure', description: '', amount: '', currency: 'USD', frequency: 'monthly', is_recurring: true, date_added: new Date().toISOString().split('T')[0], notes: '' });
      loadData();
    } catch { toast.error('Failed to save expense'); }
  };

  const handleEdit = (expense) => {
    setForm(expense);
    setEditingId(expense.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      title: 'Delete Expense',
      message: 'Delete this expense? This cannot be undone.',
      confirmLabel: 'Delete',
      confirmColor: 'var(--error)',
      onConfirm: async () => {
        try {
          await api.delete(`/api/admin/expenses/${id}`);
          toast.success('Expense deleted');
          loadData();
        } catch { toast.error('Failed to delete'); }
        setConfirmDialog(null);
      }
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 24, fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <DollarSign size={26} color="var(--accent-primary)" /> Expenses
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>Track your business expenses</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={loadData} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ category: 'infrastructure', description: '', amount: '', currency: 'USD', frequency: 'monthly', is_recurring: true, date_added: new Date().toISOString().split('T')[0], notes: '' }); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent-primary)', background: 'var(--accent-glow)', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Add Expense
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', marginBottom: 'var(--space-xl)' }}>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Category</label>
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
                <option value="infrastructure">Infrastructure</option>
                <option value="domain">Domain</option>
                <option value="software">Software</option>
                <option value="api">API</option>
                <option value="email">Email</option>
                <option value="marketing">Marketing</option>
                <option value="legal">Legal</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Amount (USD)</label>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Frequency</label>
              <select value={form.frequency} onChange={(e) => setForm({...form, frequency: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="onetime">One-time</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="submit" style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {editingId ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' }}>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Frequency</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>No expenses recorded</td></tr>
              ) : (
                expenses.map(exp => (
                  <tr key={exp.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={tdStyle}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{exp.category}</span></td>
                    <td style={tdStyle}>{exp.description}</td>
                    <td style={tdStyle}>${parseFloat(exp.amount).toFixed(2)}</td>
                    <td style={tdStyle}>{exp.frequency}</td>
                    <td style={tdStyle}>{exp.date_added}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => handleEdit(exp)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><Edit size={12} /></button>
                        <button onClick={() => handleDelete(exp.id)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--error)', background: 'transparent', color: 'var(--error)', cursor: 'pointer' }}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {confirmDialog && (
        <ConfirmModal
          open={!!confirmDialog}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmColor={confirmDialog.confirmColor}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 14px', whiteSpace: 'nowrap' };